import type { Result } from "../types/Result"
import { ArrayToIterable } from "./ArrayToIterable"
import { First } from "./First"
import { SafePromise } from "./SafePromise"

/**
 * Peeks at the first value of an async generator without consuming it.
 * Returns both the first value and a reconstituted stream that includes it.
 *
 * @param iterable - The async generator to peek into
 * @returns A promise resolving to the first value (via Result) and the full stream
 *
 * @example
 * ```ts
 * import { Peek } from "@pdfking/tfunc"
 *
 * async function* generateNumbers() {
 *   for (let i = 0; i < 5; i++) {
 *     yield i;
 *   }
 * }
 *
 * const { value, stream } = await Peek(generateNumbers());
 * console.log(value); // 0
 *
 * for await (const n of stream) {
 *   console.log(n); // 0, 1, 2, 3, 4
 * }
 * ```
 */
export const Peek = <T>(
  iterable: AsyncGenerator<T>
): Promise<{ stream: AsyncGenerator<T> } & Result<T>> =>
  SafePromise(First(iterable)).then(result => ({
    ...result,
    stream: ArrayToIterable(result.value ? [result.value] : [], iterable),
  }))
